import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import Label from '@/common/components/Label.tsx'
import Textarea from '@/common/components/Textarea.tsx'
import { PlusIcon } from '@/common/icons/arrows.tsx'
import usePermissionAlert from '@/hook/usePermissionAlert'
import { useUserPrivileges } from '@/hook/useUserPrivileges'
import { usePostProviderAffiliation } from '@/queries/providers/affiliations/hooks.ts'
import { ProviderAffiliationLogoInput } from '@/queries/providers/affiliations/schema.ts'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shad/ui/dialog.tsx'
import { Switch } from '@/shad/ui/switch'
import { useToast } from '@/shad/ui/toast/use-toast.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { useState } from 'react'
import { z } from 'zod'
import AffiliationLogoField from './AffiliationLogoField.tsx'
import { Form } from '@/app/common/components/Form.tsx'
import FormErrorMessage from '@/app/common/components/FormErrorMessage.tsx'

const createAffiliationSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(255, 'Name must be 255 characters or less'),
  description: z
    .string()
    .trim()
    .max(255, 'Description must be 255 characters or less')
    .optional(),
  logo_image: z.custom<ProviderAffiliationLogoInput>().optional(),
  status: z.boolean(),
})

type AddAffiliationFormValues = {
  name: string
  description?: string
  logo_image?: ProviderAffiliationLogoInput
  status: boolean
}

type Props = {
  onAddAffiliationSuccess: () => void
}

const defaultValues: AddAffiliationFormValues = {
  name: '',
  description: '',
  logo_image: null,
  status: true,
}

export default function AddAffiliationPopup({
  onAddAffiliationSuccess,
}: Props) {
  const [open, setOpen] = useState(false)
  const { toast } = useToast()
  const privileges = useUserPrivileges()
  const { showPermissionDeniedMessage } = usePermissionAlert()

  const form = useForm<AddAffiliationFormValues>({
    resolver: zodResolver(createAffiliationSchema),
    defaultValues,
  })

  const createAffiliationMutation = usePostProviderAffiliation({
    onSuccess: () => {
      toast({
        title: 'Affiliation Created',
        description: 'The affiliation has been created successfully.',
        variant: 'default',
      })
      onAddAffiliationSuccess()
      setOpen(false)
      form.reset(defaultValues)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const handleClose = () => {
    setOpen(false)
    form.reset(defaultValues)
  }

  const onSubmit = (data: AddAffiliationFormValues) => {
    createAffiliationMutation.mutate({
      name: data.name,
      description: data.description?.trim() ? data.description.trim() : null,
      logo_image: data.logo_image ?? null,
      status: data.status ? 1 : 0,
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (createAffiliationMutation.isPending && !nextOpen) {
          return
        }
        setOpen(nextOpen)

        if (!nextOpen) {
          form.reset(defaultValues)
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          className="gap-2"
          onClick={(event) => {
            if (!privileges.user.provider_management_create) {
              event.preventDefault()
              void showPermissionDeniedMessage()
            }
          }}
        >
          <PlusIcon />
          Add Affiliation
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add Affiliation</DialogTitle>
        </DialogHeader>

        <Form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="affiliation-name" isRequired>
              Name
            </Label>
            <Input
              id="affiliation-name"
              {...form.register('name')}
              disabled={createAffiliationMutation.isPending}
              aria-invalid={form.formState.errors.name ? 'true' : 'false'}
            />
            <FormErrorMessage message={form.formState.errors.name?.message} />
          </div>
          <div>
            <Label htmlFor="affiliation-description">
              Description (Optional)
            </Label>
            <Textarea
              id="affiliation-description"
              {...form.register('description')}
              name="description"
              rows={4}
              placeholder="Enter description"
              disabled={createAffiliationMutation.isPending}
            />
            <FormErrorMessage
              message={form.formState.errors.description?.message}
            />
          </div>
          <div>
            <Controller
              control={form.control}
              name="logo_image"
              render={({ field }) => (
                <AffiliationLogoField
                  value={field.value}
                  onChange={field.onChange}
                  disabled={createAffiliationMutation.isPending}
                />
              )}
            />
          </div>

          <Controller
            control={form.control}
            name="status"
            render={({ field }) => (
              <div className="flex items-center justify-between rounded-md border border-gray-200 p-4">
                <div>
                  <Label htmlFor="affiliation-status">Status</Label>
                  <p className="text-sm text-gray-600">
                    {field.value ? 'Active' : 'Inactive'}
                  </p>
                </div>
                <Switch
                  id="affiliation-status"
                  checked={field.value}
                  disabled={createAffiliationMutation.isPending}
                  onCheckedChange={field.onChange}
                />
              </div>
            )}
          />

          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={createAffiliationMutation.isPending}
              onClick={handleClose}
            >
              Cancel
            </Button>

            <div>
              <SubmitButton
                buttonTxt="Add Affiliation"
                submittingText="Creating..."
                status={createAffiliationMutation.status}
              />
            </div>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
